package com.milta.feature.route.domain.entity

/**
 * Detail of a single route (MIL-38 — `GET /api/v1/routes/{id}`), shared by a Driver viewing their own
 * route and an Enterprise manager viewing any route of their tenant.
 *
 * Replaces the previous `driverId` + flat `deliveries: List<Delivery>` shape that mirrored the old,
 * now-dead `GET /routes` (list) response: that same endpoint path returns [TeamRoutesOverview] instead
 * (see `TeamRouteOverview.kt`) — kept as one repurposed entity rather than a duplicate `RouteDetail`
 * type since the two response shapes never coexisted on the wire.
 */
data class Route(
    val id: String,
    val date: String,
    val status: RouteStatus,
    val driverFirstName: String,
    val driverLastName: String,
    /** ISO-8601 datetime, null while the route hasn't been started yet. */
    val startedAt: String?,
    val deliveries: List<Delivery>,
) {
    /** Numéro court dérivé de l'id (pas de champ numéro côté domaine) — mirrors [RouteSummary.number]. */
    val number: String get() = id.takeLast(4).uppercase()

    val completedStopsCount: Int get() = deliveries.count { it.status == DeliveryStatus.Completed }
    val totalStopsCount: Int get() = deliveries.size

    /** Progression 0f..1f — mirrors [RouteSummary.progress]. */
    val progress: Float get() = if (totalStopsCount == 0) 0f else completedStopsCount.toFloat() / totalStopsCount

    /** MIL-45 — true when at least one stop still lacks GPS coordinates; gates the "Regéocoder" action.
     * Excludes [RouteStatus.Completed] routes: mirrors RegeocodeRouteHandler's own business rule
     * (backend throws ConflictException once the route is Completed — stops are a recorded outcome,
     * regeocoding them has no operational benefit) — without this guard the banner and per-stop
     * warning kept pointing at a "Regéocoder" action that would always fail once the route finished. */
    val hasGeocodeIssues: Boolean
        get() = status != RouteStatus.Completed && deliveries.any { it.geocodeStatus != GeocodeStatus.Geocoded }

    /**
     * MIL-38 gap fix — mirrors [RouteSummary.currentStopId] (driver-side): the first delivery
     * still Pending while the route is InProgress. Purely derived here too — no "en route" status
     * is persisted per delivery server-side.
     */
    val currentDeliveryId: String?
        get() = if (status == RouteStatus.InProgress) {
            deliveries.firstOrNull { it.status == DeliveryStatus.Pending }?.id
        } else {
            null
        }
}
